import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Checkbox, FormControlLabel, InputLabel, MenuItem, Select, Stack, TablePagination, Tooltip, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useConfirm } from '../../alert/AlertContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LoadingMask from '../components/LoadingMask';
import { millisecondsToTimestamp } from '../../util/TimeUtil';
import ErrorLogEntryDialog from './ErrorLogEntryDialog';

const NewDot = styled.div`
    height: 10px;
    width: 10px;
    background-color: #1E90FF;
    border-radius: 50%;
    display: inline-block;
`;

const containers = ['All', 'Dashboard', 'Discord Bot', 'Forge Mod', 'Website'];
const severityLevels = ['All', 'WARNING', 'ERROR'];

export default function ErrorLogList() {
    const [errors, setErrors] = useState([]);
    const [search, setSearch] = useState('');
    const [includeSeen, setIncludeSeen] = useState(false);
    const [container, setContainer] = useState('All');
    const [severity, setSeverity] = useState('All');
    const [startDate, setStartDate] = useState(0);
    const [endDate, setEndDate] = useState(Date.now());
    const [errorToShow, setErrorToShow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [itemCount, setItemCount] = useState(0);
    const confirm = useConfirm();
    const [detailsExpanded, setDetailsExpanded] = useState(false);
    const [errorLogStatus, setErrorLogStatus] = useState("NOT_NEAR_LIMIT");

    useEffect(() => {
        const fetchErrorLogStatus = async () => {
            const response = await axios.get("/errorlog/status");
            setErrorLogStatus(response.data);
        };

        fetchErrorLogStatus();
    }, []);

    useEffect(() => {
        reload();
    }, []);

    const reload = () => {
        loadNewPage(page, rowsPerPage, includeSeen, container, severity, startDate, endDate, search);
    };

    const loadNewPage = async (page, rowsPerPage, includeSeen, container, severity, startDate, endDate, search) => {
        setLoading(true);
        const params = new URLSearchParams({
            pageNumber: page,
            itemsPerPage: rowsPerPage,
            includeSeen,
            startTime: startDate,
            endTime: endDate,
            search,
        });
        if (container !== 'All') params.append('containerFilter', container);
        if (severity !== 'All') params.append('severity', severity);

        try {
            const response = await axios.get(`/errorlog?${params.toString()}`);
            setErrors(response.data.items);
            setPage(response.data.pageNumber);
            setRowsPerPage(response.data.itemsPerPage);
            setItemCount(response.data.totalItems);
        } catch {
            toast.error('Error fetching error logs');
        }
        setLoading(false);
    };

    const handleClick = (error) => {
        if (error.stackTrace) setErrorToShow(error);
        axios.post(`/errorlog/seen/${error.id}`);
    };

    const handleSeenAllClick = () => {
        confirm('Mark All As Seen', 'This will mark all errors as seen. Continue?', () => {
            setLoading(true);
            axios.post('/errorlog/seen/all').then((response) => {
                toast.success(`Marked ${response.data} errors as seen`);
                reload();
                setLoading(false);
            });
        });
    };

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
        loadNewPage(newPage, rowsPerPage, includeSeen, container, severity, startDate, endDate, search);
    };

    const handleChangeRowsPerPage = (event) => {
        const value = parseInt(event.target.value, 10);
        setRowsPerPage(value);
        setPage(0);
        loadNewPage(0, value, includeSeen, container, severity, startDate, endDate, search);
    };

    const onSearchClick = () => {
        setDetailsExpanded(false);
        loadNewPage(page, rowsPerPage, includeSeen, container, severity, startDate, endDate, search);
    };

    const handleKeyDown = (e) => e.key === 'Enter' && onSearchClick();
    const handleDateChange = (setter) => (e) => setter(new Date(e.target.value).getTime() || null);

    const startDateString = new Date(startDate).toISOString().split('T')[0];
    const endDateString = new Date(endDate).toISOString().split('T')[0];

    return (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            {errorLogStatus === "NEAR_LIMIT" && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    The error log is nearing its limit. Please review and clear unnecessary logs.
                </Alert>
            )}

            {errorLogStatus === "AT_LIMIT" && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    The error log has reached its limit. New errors may not be logged.
                </Alert>
            )}

            <Accordion expanded={detailsExpanded} onChange={(_, exp) => setDetailsExpanded(exp)} sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                boxShadow: 'none',
            }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant='h6'>Search Filters</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={2}>
                        <FormControlLabel
                            control={<Checkbox checked={includeSeen} onChange={(e) => setIncludeSeen(e.target.checked)} />}
                            label="Include Seen"
                        />
                        <Stack direction='row' spacing={2}>
                            <Box sx={{ minWidth: 160 }}>
                                <InputLabel>Container</InputLabel>
                                <Select
                                    value={container}
                                    onChange={(e) => setContainer(e.target.value)}
                                    fullWidth
                                    variant="standard"
                                >
                                    {containers.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </Select>
                            </Box>
                            <Box sx={{ minWidth: 160 }}>
                                <InputLabel>Severity</InputLabel>
                                <Select
                                    value={severity}
                                    onChange={(e) => setSeverity(e.target.value)}
                                    fullWidth
                                    variant="standard"
                                >
                                    {severityLevels.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Start Date"
                                type="date"
                                value={startDateString}
                                onChange={handleDateChange(setStartDate)}
                                InputLabelProps={{ shrink: true }}
                                variant="standard"
                                fullWidth
                            />
                            <TextField
                                label="End Date"
                                type="date"
                                value={endDateString}
                                onChange={handleDateChange(setEndDate)}
                                InputLabelProps={{ shrink: true }}
                                variant="standard"
                                fullWidth
                            />
                        </Stack>
                        <TextField
                            label="Search Text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                            variant="standard"
                            fullWidth
                        />
                        <Button onClick={onSearchClick} variant="contained" sx={{ alignSelf: 'flex-start' }}>
                            Search
                        </Button>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {errorToShow && (
                <ErrorLogEntryDialog
                    title={errorToShow.message}
                    message={errorToShow.stackTrace}
                    onClose={() => setErrorToShow(null)}
                />
            )}

            <TableContainer sx={{ position: 'relative', overflowY: 'auto', flexGrow: 1 }}>
                <LoadingMask visible={loading} fullScreen={false} />
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Severity</TableCell>
                            <TableCell>Container</TableCell>
                            <TableCell>Message</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {errors.length ? (
                            errors.map((error, index) => (
                                <TableRow
                                    key={error.id}
                                    hover
                                    onClick={() => handleClick(error)}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                                    }}
                                >
                                    <TableCell>
                                        {!error.seen && (
                                            <Tooltip title="New"><NewDot /></Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>{millisecondsToTimestamp(error.timeStamp)}</TableCell>
                                    <TableCell>{error.severity}</TableCell>
                                    <TableCell>{error.container}</TableCell>
                                    <TableCell>{error.message}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography>No Results</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Stack direction="row" alignItems="center" sx={{ mt: 1 }}>
                <Button onClick={handleSeenAllClick} variant="text" sx={{ mr: 'auto' }}>
                    Mark All As Seen
                </Button>
                <TablePagination
                    component="div"
                    count={itemCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Stack>
        </Box>
    );
}
